/**
 * Validation utilities for file and folder names
 * 
 * Provides validation functions for the FileExplorer component to ensure
 * names are valid before performing rename or create operations.
 * 
 * @module validation
 */

/**
 * Maximum allowed length for file/folder names
 */
export const MAX_NAME_LENGTH = 255;

/**
 * Set of characters that are invalid in file/folder names
 * These characters are not allowed in Windows file systems and can cause issues in S3 keys
 */
export const INVALID_CHARACTERS = new Set(['/', '\\', ':', '*', '?', '"', '<', '>', '|']);

/**
 * Reserved names that cannot be used for files or folders
 * These are Windows reserved device names that can cause issues
 */
export const RESERVED_NAMES = new Set([
  'CON', 'PRN', 'AUX', 'NUL',
  'COM1', 'COM2', 'COM3', 'COM4', 'COM5', 'COM6', 'COM7', 'COM8', 'COM9',
  'LPT1', 'LPT2', 'LPT3', 'LPT4', 'LPT5', 'LPT6', 'LPT7', 'LPT8', 'LPT9'
]);

/**
 * Checks if a character is a control character (ASCII 0-31)
 * @param {string} char - Single character to check
 * @returns {boolean} True if the character is a control character
 */
export function isControlCharacter(char) {
  const code = char.charCodeAt(0);
  return code >= 0 && code <= 31;
}

/**
 * Validates a file or folder name
 * 
 * Checks for:
 * - Empty names
 * - Names that are only whitespace
 * - Invalid characters (/, \, :, *, ?, ", <, >, |)
 * - Control characters (ASCII 0-31)
 * - Names exceeding maximum length (255 characters)
 * - Reserved names (Windows device names)
 * 
 * @param {string} name - The name to validate
 * @returns {Object} ValidationResult with valid boolean and optional error message
 * @property {boolean} valid - Whether the name is valid
 * @property {string} [error] - Error message if invalid
 * 
 * @example
 * validateName('my-file.txt') // { valid: true }
 * validateName('file:name.txt') // { valid: false, error: 'Name contains invalid characters: :' }
 */
export function validateName(name) {
  // Check for null/undefined
  if (name === null || name === undefined) {
    return {
      valid: false,
      error: 'Name cannot be empty'
    };
  }

  // Convert to string if not already
  const nameStr = String(name);

  // Check for empty name
  if (nameStr.length === 0) {
    return {
      valid: false,
      error: 'Name cannot be empty'
    };
  }

  // Check for whitespace-only name
  if (nameStr.trim().length === 0) {
    return {
      valid: false,
      error: 'Name cannot be empty'
    };
  }

  // Check for name too long
  if (nameStr.length > MAX_NAME_LENGTH) {
    return {
      valid: false,
      error: `Name exceeds maximum length of ${MAX_NAME_LENGTH} characters`
    };
  }

  // Check for invalid characters and control characters
  const foundInvalidChars = [];
  
  for (const char of nameStr) {
    if (INVALID_CHARACTERS.has(char)) {
      if (!foundInvalidChars.includes(char)) {
        foundInvalidChars.push(char);
      }
    } else if (isControlCharacter(char)) {
      // Represent control characters in a readable way
      const charCode = char.charCodeAt(0);
      const controlCharRepr = `\\x${charCode.toString(16).padStart(2, '0')}`;
      if (!foundInvalidChars.includes(controlCharRepr)) {
        foundInvalidChars.push(controlCharRepr);
      }
    }
  }

  if (foundInvalidChars.length > 0) {
    return {
      valid: false,
      error: `Name contains invalid characters: ${foundInvalidChars.join(', ')}`
    };
  }

  // Check for reserved names (case-insensitive, with or without extension)
  const baseName = nameStr.split('.')[0].toUpperCase();
  if (RESERVED_NAMES.has(baseName)) {
    return {
      valid: false,
      error: 'This name is reserved and cannot be used'
    };
  }

  return { valid: true };
}

/**
 * Checks if a name already exists in a list of existing items
 * 
 * Performs case-insensitive comparison to prevent issues on case-insensitive
 * file systems and to provide a better user experience.
 * 
 * @param {string} name - The name to check for duplicates
 * @param {Array<string|Object>} existingItems - Array of existing item names or objects with name/key property
 * @returns {Object} ValidationResult with valid boolean and optional error message
 * @property {boolean} valid - Whether the name is unique (no duplicates)
 * @property {string} [error] - Error message if duplicate found
 * 
 * @example
 * checkDuplicateName('file.txt', ['other.txt', 'document.pdf']) // { valid: true }
 * checkDuplicateName('file.txt', ['FILE.txt', 'other.txt']) // { valid: false, error: 'An item with this name already exists' }
 * checkDuplicateName('file.txt', [{ name: 'file.txt' }]) // { valid: false, error: 'An item with this name already exists' }
 */
export function checkDuplicateName(name, existingItems) {
  // Handle null/undefined name
  if (name === null || name === undefined) {
    return { valid: true }; // Let validateName handle empty name validation
  }

  // Handle null/undefined/empty existingItems
  if (!existingItems || !Array.isArray(existingItems) || existingItems.length === 0) {
    return { valid: true };
  }

  const nameStr = String(name).toLowerCase();

  for (const item of existingItems) {
    let itemName;
    
    if (typeof item === 'string') {
      itemName = item;
    } else if (item && typeof item === 'object') {
      // Support objects with name or key property
      itemName = item.name || item.key || '';
      // If key is a path, extract the filename
      if (itemName.includes('/')) {
        itemName = itemName.split('/').pop();
      }
    } else {
      continue;
    }

    if (String(itemName).toLowerCase() === nameStr) {
      return {
        valid: false,
        error: 'An item with this name already exists'
      };
    }
  }

  return { valid: true };
}

/**
 * Performs complete validation of a name including duplicate check
 * 
 * Combines validateName and checkDuplicateName for convenience.
 * First validates the name format, then checks for duplicates.
 * 
 * @param {string} name - The name to validate
 * @param {Array<string|Object>} existingItems - Array of existing item names or objects
 * @returns {Object} ValidationResult with valid boolean and optional error message
 * @property {boolean} valid - Whether the name is valid and unique
 * @property {string} [error] - Error message if invalid or duplicate
 * 
 * @example
 * validateNameWithDuplicateCheck('new-file.txt', ['other.txt']) // { valid: true }
 * validateNameWithDuplicateCheck('', ['other.txt']) // { valid: false, error: 'Name cannot be empty' }
 * validateNameWithDuplicateCheck('other.txt', ['other.txt']) // { valid: false, error: 'An item with this name already exists' }
 */
export function validateNameWithDuplicateCheck(name, existingItems) {
  // First validate the name format
  const nameValidation = validateName(name);
  if (!nameValidation.valid) {
    return nameValidation;
  }

  // Then check for duplicates
  return checkDuplicateName(name, existingItems);
}
